import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/stores/AuthContext'

const DASHBOARD_URL = '/dashboard'
const LOGIN_URL = '/dashboard/login'
const REGISTER_URL = '/dashboard/register'

interface NavLeaf {
    to: string
    label: string
    description?: string
}

interface NavGroup {
    label: string
    children: NavLeaf[]
    wide?: boolean
}

type NavItem = NavLeaf | NavGroup

const isGroup = (n: NavItem): n is NavGroup => 'children' in n

const NAV: NavItem[] = [
    { to: '/', label: 'Home' },
    {
        label: 'About',
        children: [
            { to: '/about-us', label: 'About Us', description: 'The Centre & its mandate' },
            { to: '/vision-mission', label: 'Vision & Mission' },
            { to: '/board-of-trustees', label: 'Board of Trustees' },
            { to: '/events-collaboration', label: 'Events & Collaboration' },
            { to: '/call-for-academic-collaborations', label: 'Academic Collaborations' },
            { to: '/faqs', label: 'FAQs' }
        ]
    },
    {
        label: 'Journal',
        children: [
            { to: '/about-the-journal', label: 'About the Journal', description: 'Indian Studies Review' },
            { to: '/journal-particulars', label: 'Journal Particulars' },
            { to: '/editorial-board', label: 'Editorial Board' },
            { to: '/reviewers-list', label: "Reviewer's List" },
            { to: '/editorial-workflow', label: 'Editorial Workflow' },
            { to: '/article-processing-charge', label: 'Article Processing Charge' }
        ]
    },
    {
        label: 'For Authors',
        children: [
            { to: '/submission', label: 'Submit a Manuscript' },
            { to: '/guidelines', label: 'Submission Guidelines' },
            { to: '/instructions', label: 'Author Instructions' },
            { to: '/plagiarism-ai-policy', label: 'Plagiarism & AI Policy' }
        ]
    },
    {
        label: 'Issues',
        children: [
            { to: '/current-issue', label: 'Current Issue', description: 'Vol 7 · No. 1' },
            { to: '/archives', label: 'Archives' },
            { to: '/call-for-papers', label: 'Call for Papers' },
            { to: '/subscription', label: 'Subscription' }
        ]
    },
    {
        label: 'Discussion Forum',
        children: [
            { to: '/discussion/perspective', label: 'Perspective', description: 'Opinion & position pieces' },
            { to: '/discussion/commentary', label: 'Commentary', description: 'Short responses & commentary' }
        ]
    },
    {
        label: 'Policies',
        wide: true,
        children: [
            { to: '/reviewers-guidelines', label: "Reviewer's Guidelines" },
            { to: '/scope-ethical-guidelines', label: 'Scope & Ethical Guidelines' },
            { to: '/publication-ethics', label: 'Publication Ethics & Integrity' },
            { to: '/peer-review-policy', label: 'Peer Review Policy' },
            { to: '/editorial-decision-appeals', label: 'Editorial Decision Appeals' },
            { to: '/correction-retraction', label: 'Correction & Retraction' },
            { to: '/author-fee-policy', label: 'Author Fee Policy' },
            { to: '/plagiarism-policy', label: 'Plagiarism Policy' },
            { to: '/privacy-statement', label: 'Privacy Statement' },
            { to: '/ugc-guidelines', label: 'UGC Guidelines' },
            { to: '/copyright-licensing', label: 'Copyright & Licensing' },
            { to: '/cope', label: 'COPE' }
        ]
    },
    { to: '/contact-us', label: 'Contact' }
]

export default function SiteHeader() {
    const { user } = useAuth()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
    const navRef = useRef<HTMLElement | null>(null)
    const closeTimer = useRef<number | null>(null)
    const location = useLocation()

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8)
        onScroll()
        window.addEventListener('scroll', onScroll)
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        setActiveDropdown(null)
        setOpen(false)
        setMobileExpanded(null)
    }, [location.pathname])

    useEffect(() => {
        if (!open) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
        document.addEventListener('keydown', onEsc)
        return () => {
            document.body.style.overflow = prev
            document.removeEventListener('keydown', onEsc)
        }
    }, [open])

    useEffect(() => {
        if (!activeDropdown) return
        const onClick = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setActiveDropdown(null)
            }
        }
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveDropdown(null)
        }
        document.addEventListener('mousedown', onClick)
        document.addEventListener('keydown', onEsc)
        return () => {
            document.removeEventListener('mousedown', onClick)
            document.removeEventListener('keydown', onEsc)
        }
    }, [activeDropdown])

    const scheduleClose = () => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current)
        closeTimer.current = window.setTimeout(() => setActiveDropdown(null), 140)
    }
    const cancelClose = () => {
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const isGroupActive = (g: NavGroup) =>
        g.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'))

    const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    })

    return (
        <header className="sticky top-0 z-40">
            <div
                className="hidden md:block"
                style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-200)' }}
            >
                <div className="container-prose flex items-center justify-between text-[11px] tracking-[0.2em] uppercase py-1.5">
                    <div className="opacity-70">CSPG · ISSN 2583-004X · Bi-Annual</div>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/call-for-papers"
                            className="font-semibold hover:text-[var(--color-gold-300)] transition-colors"
                            style={{ color: 'var(--color-gold-300)' }}
                        >
                            Vol 7 · Issue 2 — Submissions Open
                        </Link>
                        <span className="opacity-40">·</span>
                        <span className="opacity-60">{today}</span>
                    </div>
                </div>
            </div>

            <div
                className="transition-all duration-300"
                style={{
                    background: scrolled ? 'rgba(253, 251, 246, 0.96)' : 'var(--color-paper-50)',
                    backdropFilter: scrolled ? 'saturate(140%) blur(10px)' : undefined,
                    borderBottom: '1px solid var(--color-rule-strong)',
                    boxShadow: scrolled ? '0 6px 20px -16px rgba(20,16,8,0.5)' : undefined
                }}
            >
                <div className="container-prose flex items-center justify-between gap-3 py-3 xl:grid xl:grid-cols-[1fr_auto_1fr]">
                    <Link
                        to="/"
                        className="inline-flex items-center shrink-0 group justify-self-start"
                        aria-label="Indian Studies Review — home"
                    >
                        <img
                            src="/brand/cspg-logo.png"
                            alt="Centre for Study of Politics and Governance"
                            className="h-8 md:h-10 w-auto max-w-none shrink-0"
                        />
                    </Link>

                    <nav ref={navRef} className="hidden xl:flex justify-self-center items-center gap-0 text-[12.5px]">
                        {NAV.map((item) => {
                            if (!isGroup(item)) {
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === '/'}
                                        className={({ isActive }) =>
                                            `relative whitespace-nowrap px-2 py-2 font-medium tracking-wide transition-colors ${
                                                isActive
                                                    ? 'text-[var(--color-gold-700)]'
                                                    : 'text-[var(--color-ink-500)] hover:text-[var(--color-ink-700)]'
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <span className="relative">
                                                {item.label}
                                                <span
                                                    aria-hidden
                                                    className="absolute left-0 right-0 -bottom-1 h-px transition-transform"
                                                    style={{
                                                        background: 'var(--color-gold-500)',
                                                        transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                                                        transformOrigin: 'left'
                                                    }}
                                                />
                                            </span>
                                        )}
                                    </NavLink>
                                )
                            }

                            const active = activeDropdown === item.label
                            const groupActive = isGroupActive(item)
                            return (
                                <div
                                    key={item.label}
                                    className="relative"
                                    onMouseEnter={() => {
                                        cancelClose()
                                        setActiveDropdown(item.label)
                                    }}
                                    onMouseLeave={scheduleClose}
                                >
                                    <button
                                        type="button"
                                        aria-haspopup="menu"
                                        aria-expanded={active}
                                        onClick={() => setActiveDropdown(active ? null : item.label)}
                                        className={`inline-flex items-center gap-1 whitespace-nowrap px-2 py-2 font-medium tracking-wide transition-colors ${
                                            groupActive
                                                ? 'text-[var(--color-gold-700)]'
                                                : 'text-[var(--color-ink-500)] hover:text-[var(--color-ink-700)]'
                                        }`}
                                    >
                                        <span className="relative">
                                            {item.label}
                                            <span
                                                aria-hidden
                                                className="absolute left-0 right-0 -bottom-1 h-px transition-transform"
                                                style={{
                                                    background: 'var(--color-gold-500)',
                                                    transform: groupActive ? 'scaleX(1)' : 'scaleX(0)',
                                                    transformOrigin: 'left'
                                                }}
                                            />
                                        </span>
                                        <svg
                                            aria-hidden
                                            width="9"
                                            height="9"
                                            viewBox="0 0 10 6"
                                            className="opacity-60 transition-transform"
                                            style={{ transform: active ? 'rotate(180deg)' : 'rotate(0)' }}
                                        >
                                            <path
                                                d="M1 1l4 4 4-4"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>

                                    {active && (
                                        <div
                                            role="menu"
                                            className={`absolute top-full pt-2 ${item.wide ? 'right-0' : 'left-0'}`}
                                            style={{ minWidth: item.wide ? '36rem' : '14.5rem' }}
                                            onMouseEnter={cancelClose}
                                            onMouseLeave={scheduleClose}
                                        >
                                            <div
                                                className={`py-2 ${item.wide ? 'grid grid-cols-2 gap-x-1' : ''}`}
                                                style={{
                                                    background: 'var(--color-paper-50)',
                                                    border: '1px solid var(--color-rule)',
                                                    borderTop: '2px solid var(--color-gold-500)',
                                                    boxShadow: '0 12px 28px -16px rgba(13, 27, 42, 0.18)'
                                                }}
                                            >
                                                {item.children.map((c) => (
                                                    <NavLink
                                                        key={c.to}
                                                        to={c.to}
                                                        role="menuitem"
                                                        className={({ isActive }) =>
                                                            `block px-4 py-2 leading-tight transition-colors ${
                                                                isActive
                                                                    ? 'text-[var(--color-gold-700)] bg-[rgba(176,143,47,0.06)]'
                                                                    : 'text-[var(--color-ink-600)] hover:text-[var(--color-ink-700)] hover:bg-[rgba(13,27,42,0.03)]'
                                                            }`
                                                        }
                                                    >
                                                        <div className="font-medium text-[13px]">{c.label}</div>
                                                        {c.description && (
                                                            <div
                                                                className="text-[10.5px] mt-0.5"
                                                                style={{
                                                                    color: 'var(--color-ink-300)',
                                                                    letterSpacing: '0.02em'
                                                                }}
                                                            >
                                                                {c.description}
                                                            </div>
                                                        )}
                                                    </NavLink>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </nav>

                    <div className="flex items-center gap-2 shrink-0 justify-self-end">
                    <div className="hidden xl:flex items-center gap-2.5 shrink-0 whitespace-nowrap">
                        {user ? (
                            <Link to={DASHBOARD_URL} className="btn btn-primary text-xs">
                                Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link to={LOGIN_URL} className="btn btn-ghost text-xs">
                                    Sign In
                                </Link>
                                <Link to={REGISTER_URL} className="btn btn-gold text-xs">
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>

                    <button
                        aria-label="Toggle menu"
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="xl:hidden p-2"
                        style={{ color: 'var(--color-ink-600)' }}
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            {open ? (
                                <path d="M6 6l12 12M18 6L6 18" strokeWidth="1.5" strokeLinecap="round" />
                            ) : (
                                <path d="M3 7h18M3 12h18M3 17h18" strokeWidth="1.5" strokeLinecap="round" />
                            )}
                        </svg>
                    </button>
                    </div>
                </div>
            </div>

            {createPortal(
                <div className="xl:hidden" aria-hidden={!open}>
                    <div
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 z-[60] transition-opacity duration-300"
                        style={{
                            background: 'rgba(13,11,8,0.5)',
                            backdropFilter: 'blur(2px)',
                            opacity: open ? 1 : 0,
                            pointerEvents: open ? 'auto' : 'none'
                        }}
                    />

                    <aside
                        role="dialog"
                        aria-modal="true"
                        aria-label="Menu"
                        className="fixed top-0 right-0 z-[70] h-full w-[86%] max-w-sm flex flex-col transition-transform duration-300 ease-out"
                        style={{
                            background: 'var(--color-paper-50)',
                            borderLeft: '1px solid var(--color-rule)',
                            boxShadow: '-18px 0 40px -24px rgba(13,11,8,0.5)',
                            transform: open ? 'translateX(0)' : 'translateX(100%)'
                        }}
                    >
                        <div
                            className="flex items-center justify-between px-5 py-3.5 shrink-0"
                            style={{ borderBottom: '1px solid var(--color-rule)' }}
                        >
                            <Link to="/" onClick={() => setOpen(false)} aria-label="Home" className="inline-flex items-center">
                                <img src="/brand/cspg-logo.png" alt="CSPG" className="h-8 w-auto" />
                            </Link>
                            <button
                                type="button"
                                aria-label="Close menu"
                                onClick={() => setOpen(false)}
                                className="p-2 -mr-2"
                                style={{ color: 'var(--color-ink-600)' }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M6 6l12 12M18 6L6 18" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        <nav className="flex-1 overflow-y-auto px-5 py-3 flex flex-col">
                            {NAV.map((item) => {
                                if (!isGroup(item)) {
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setOpen(false)}
                                            className="py-3 text-[14px] font-medium border-b"
                                            style={{
                                                color: 'var(--color-ink-700)',
                                                borderColor: 'var(--color-rule)'
                                            }}
                                        >
                                            {item.label}
                                        </Link>
                                    )
                                }
                                const expanded = mobileExpanded === item.label
                                return (
                                    <div
                                        key={item.label}
                                        className="border-b"
                                        style={{ borderColor: 'var(--color-rule)' }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setMobileExpanded(expanded ? null : item.label)}
                                            aria-expanded={expanded}
                                            className="w-full py-3 flex items-center justify-between text-[14px] font-medium"
                                            style={{ color: 'var(--color-ink-700)' }}
                                        >
                                            {item.label}
                                            <svg
                                                aria-hidden
                                                width="10"
                                                height="10"
                                                viewBox="0 0 10 6"
                                                className="opacity-60 transition-transform"
                                                style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}
                                            >
                                                <path
                                                    d="M1 1l4 4 4-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                        <div
                                            className="overflow-hidden transition-all duration-300 ease-out"
                                            style={{ maxHeight: expanded ? `${item.children.length * 2.75 + 0.75}rem` : 0 }}
                                        >
                                            <div className="pb-3 pl-1 flex flex-col">
                                                {item.children.map((c) => (
                                                    <Link
                                                        key={c.to}
                                                        to={c.to}
                                                        onClick={() => setOpen(false)}
                                                        className="py-2 text-[13px]"
                                                        style={{ color: 'var(--color-ink-500)' }}
                                                    >
                                                        {c.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </nav>

                        <div
                            className="px-5 py-2.5 shrink-0 flex items-center justify-between text-[10.5px] uppercase font-semibold"
                            style={{ background: 'var(--color-ink-900)', color: 'var(--color-paper-200)', letterSpacing: '0.16em' }}
                        >
                            <Link
                                to="/call-for-papers"
                                onClick={() => setOpen(false)}
                                style={{ color: 'var(--color-gold-300)' }}
                            >
                                Vol 7 · Issue 2 — Submissions Open
                            </Link>
                        </div>

                        <div
                            className="px-5 py-4 shrink-0 flex flex-col gap-2"
                            style={{ borderTop: '1px solid var(--color-rule)' }}
                        >
                            {user ? (
                                <Link to={DASHBOARD_URL} onClick={() => setOpen(false)} className="btn btn-primary text-xs">
                                    Dashboard
                                </Link>
                            ) : (
                                <>
                                    <Link to={LOGIN_URL} onClick={() => setOpen(false)} className="btn btn-ghost text-xs">
                                        Sign In
                                    </Link>
                                    <Link to={REGISTER_URL} onClick={() => setOpen(false)} className="btn btn-gold text-xs">
                                        Sign Up
                                    </Link>
                                </>
                            )}
                        </div>
                    </aside>
                </div>,
                document.body
            )}
        </header>
    )
}
